import React from 'react';
import { motion } from 'framer-motion';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
interface Feature {
  name: string;
  description: string;
}
interface FeatureGroup {
  title: string;
  features: Feature[];
}
interface PricingPlan {
  name: string;
  description: string;
  groups: FeatureGroup[];
  cta: string;
  popular?: boolean;
  premium?: boolean;
}
const plans: PricingPlan[] = [{
  name: 'Plan Básico',
  description: 'Ideal para vehículos particulares',
  groups: [{
    title: 'LOCALIZACIÓN',
    features: [{
      name: 'GPS en tiempo real',
      description: 'Ubicación exacta al segundo'
    }, {
      name: 'Historial de rutas',
      description: 'Consulta los últimos 90 días'
    }]
  }, {
    title: 'SEGURIDAD',
    features: [{
      name: 'Bloqueo remoto',
      description: 'Inmoviliza el vehículo desde la app'
    }, {
      name: 'Alertas inteligentes',
      description: 'Velocidad, movimiento no autorizado, encendido'
    }]
  }, {
    title: 'REPORTES',
    features: [{
      name: 'Informe de ruta diario',
      description: 'Resumen automático de recorridos'
    }, {
      name: 'APP móvil iOS & Android',
      description: 'Control desde cualquier lugar'
    }]
  }],
  cta: 'Cotizar Plan Básico'
}, {
  name: 'Plan Intermedio',
  description: 'Para flotillas pequeñas y medianas',
  groups: [{
    title: 'TODO LO DEL PLAN BÁSICO +',
    features: [{
      name: 'Todas las funciones del Plan Básico',
      description: 'incluidas'
    }]
  }, {
    title: 'MONITOREO AVANZADO',
    features: [{
      name: 'Sensor de temperatura',
      description: 'Ideal para carga refrigerada'
    }, {
      name: 'Control de combustible',
      description: 'Detecta desvíos y consumo anormal'
    }, {
      name: 'Conducción eficiente',
      description: 'Frenadas, aceleración, velocidad'
    }]
  }, {
    title: 'GESTIÓN DE FLOTILLA',
    features: [{
      name: 'Geocercas ilimitadas',
      description: 'Zonas de entrada/salida con alertas'
    }, {
      name: 'Informe de paradas',
      description: 'Tiempos, lugares y duración exacta'
    }, {
      name: 'Múltiples usuarios',
      description: 'Acceso por rol: admin, operador, cliente'
    }]
  }, {
    title: 'PLATAFORMA',
    features: [{
      name: 'Panel web avanzado',
      description: 'Dashboard completo con KPIs'
    }]
  }],
  cta: 'Cotizar Plan Intermedio',
  popular: true
}, {
  name: 'Plan Avanzado',
  description: 'Solución completa para empresas',
  groups: [{
    title: 'TODO LO DEL PLAN INTERMEDIO +',
    features: [{
      name: 'Todas las funciones del Plan Intermedio',
      description: 'incluidas'
    }]
  }, {
    title: 'VISIÓN INTELIGENTE',
    features: [{
      name: 'Dashcam HD integrada',
      description: 'Cámara frontal e interior de alta resolución'
    }, {
      name: 'Transmisión en vivo',
      description: 'Video en tiempo real desde cualquier unidad'
    }, {
      name: '1GB almacenamiento en nube',
      description: 'Evidencia ante accidentes y siniestros'
    }]
  }, {
    title: 'SEGURIDAD CON IA',
    features: [{
      name: 'Detección de fatiga DMS',
      description: 'Alerta cuando el conductor se duerme'
    }, {
      name: 'Sistema ADAS',
      description: 'Aviso de colisión frontal y cambio de carril'
    }]
  }, {
    title: 'PERSONALIZACIÓN TOTAL',
    features: [{
      name: 'Plataforma marca blanca',
      description: 'Tu logo, tu dominio, tu identidad'
    }, {
      name: 'API e Integraciones',
      description: 'Conecta con tu ERP o sistema propio'
    }]
  }],
  cta: 'Cotizar Plan Avanzado',
  premium: true
}];
export function PricingSection() {
  const {
    ref,
    isVisible
  } = useScrollAnimation();
  return <section id="planes" ref={ref} className="w-full bg-brand-primary py-20 md:py-28" data-id="element-414">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-415">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isVisible ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12 md:mb-16" data-id="element-416">
          <h2 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4" data-id="element-417">
            Planes que crecen contigo
          </h2>
          <p className="font-body text-lg text-brand-text-secondary max-w-2xl mx-auto" data-id="element-418">
            Desde rastreo básico hasta tecnología dashcam avanzada
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-start" data-id="element-419">
          {plans.map((plan, index) => <motion.div key={plan.name} initial={{
          opacity: 0,
          y: 30
        }} animate={isVisible ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: index * 0.15
        }} className={`relative group ${plan.popular ? 'md:-translate-y-4' : ''}`} data-id="element-420">
              {/* Popular Badge */}
              {plan.popular && <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10" data-id="element-421">
                  <span className="bg-brand-accent text-brand-bg font-subheading font-semibold text-sm px-4 py-1.5 rounded-full" data-id="element-422">
                    MÁS POPULAR
                  </span>
                </div>}

              <div className={`h-full rounded-2xl p-6 md:p-8 transition-all duration-300 flex flex-col ${plan.popular ? 'glass-strong border-2 border-brand-accent shadow-xl shadow-brand-accent/20 group-hover:shadow-2xl group-hover:shadow-brand-accent/30' : plan.premium ? 'bg-brand-surface border border-cyan-subtle group-hover:border-brand-accent/50' : 'glass border border-white/10 group-hover:border-brand-accent/30'} group-hover:-translate-y-2`} data-id="element-423">
                {/* Plan Header */}
                <div className="mb-6" data-id="element-424">
                  <h3 className="font-heading font-bold text-2xl text-white mb-2" data-id="element-425">
                    {plan.name}
                  </h3>
                  <p className="font-body text-brand-text-secondary" data-id="element-426">
                    {plan.description}
                  </p>
                </div>

                {/* Feature Groups */}
                <div className="space-y-0 mb-8 flex-1" data-id="element-427">
                  {plan.groups.map((group, gIdx) => <div key={group.title} data-id="element-428">
                      {/* Group Divider + Title */}
                      <div className={`${gIdx === 0 ? 'pt-0' : 'pt-5'}`} data-id="element-429">
                        <div className="border-t border-white/[0.06] mb-3" data-id="element-430" />
                        <p className="font-subheading text-[11px] font-medium tracking-[0.1em] text-brand-text-secondary/60 mb-3" data-id="element-431">
                          {group.title}
                        </p>
                      </div>

                      {/* Features */}
                      <ul className="space-y-2.5" data-id="element-432">
                        {group.features.map(feature => <li key={feature.name} className="flex items-start gap-2.5" data-id="element-433">
                            <span className="text-brand-accent text-sm leading-5 flex-shrink-0 font-mono" data-id="element-434">
                              ✓
                            </span>
                            <span className="font-body text-[13px] leading-5" data-id="element-435">
                              <span className="text-white" data-id="element-436">{feature.name}</span>
                              <span className="text-brand-text-secondary" data-id="element-437">
                                {' '}
                                — {feature.description}
                              </span>
                            </span>
                          </li>)}
                      </ul>
                    </div>)}
                </div>

                {/* CTA */}
                <a href="#contacto" className={`block w-full text-center font-subheading font-semibold py-3 rounded-lg transition-all duration-200 ${plan.popular ? 'bg-brand-accent text-brand-bg hover:bg-brand-accent/90 hover:shadow-lg hover:shadow-brand-accent/25' : 'border-2 border-brand-accent text-brand-accent hover:bg-brand-accent/10'}`} data-id="element-438">
                  {plan.cta}
                </a>
              </div>
            </motion.div>)}
        </div>

        {/* Footer Note */}
        <motion.p initial={{
        opacity: 0
      }} animate={isVisible ? {
        opacity: 1
      } : {}} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center mt-10 font-body text-brand-text-secondary" data-id="element-439">
          Instalación a domicilio disponible · Precios personalizados según
          flotilla
        </motion.p>
      </div>
    </section>;
}